import { computed } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { statsApi } from '@/api'
import type { LeaderboardEntry } from '@/api'
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'
import { normalizeTonAddress } from '@/utils/ton-address'

// UI type for top winners display
export interface TopWinner {
  address: string
  wallet: string
  totalAmount: number
  currency: string
  winCount: number
}

export function transformLeaderboard(entries: LeaderboardEntry[]): TopWinner[] {
  return entries.slice(0, 5).map((entry) => {
    const address = normalizeTonAddress(entry.user.wallet)
    return {
      address,
      wallet: `${address.slice(0, 6)}...${address.slice(-4)}`,
      totalAmount: entry.total_prize.amount,
      currency: entry.total_prize.currency.code,
      winCount: entry.total_wins,
    }
  })
}

export function useLeaderboard() {
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['stats', 'leaderboard'],
    queryFn: async () => {
      const response = await statsApi.getLeaderboard()
      if (response.success && response.data) {
        return response.data
      }
      throw new Error(response.error ?? 'Failed to fetch leaderboard')
    },
    refetchInterval: POLLING_INTERVALS.BACKEND_API,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.MEDIUM,
  })

  return {
    leaderboard: computed(() => data.value ?? []),
    topWinners: computed(() => {
      if (!data.value) return []
      return transformLeaderboard(data.value)
    }),
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load leaderboard'),
    refetch,
  }
}
